import type { ArtMetadata } from "../schedule-presets/art-metadata";
import { weekdayKeys } from "../schedule-time/schedule-dates";

export type MetadataDay = keyof ArtMetadata["days"];
export type MetadataTarget = {
  path: string;
  bind: string;
  day?: MetadataDay;
  point: number[];
  origin: number[];
  layoutRotation?: number;
};

export type MetadataTargetIdentity = Pick<MetadataTarget, "path" | "bind" | "day">;

export function metadataTargets(metadata: ArtMetadata): MetadataTarget[] {
  const globals = metadata.globals.map((element, index) => ({
    path: `globals.${index}`,
    bind: element.bind,
    point: element.position,
    origin: [0, 0],
  }));
  const days = weekdayKeys.flatMap((day) => {
    const layout = metadata.days[day];
    const prefix = `days.${day}`;
    const targets: MetadataTarget[] = [
      { path: `${prefix}.origin`, bind: "origin", day, point: layout.origin, origin: [0, 0] },
      { path: `${prefix}.date`, bind: "day.date", day, point: layout.date.offset, origin: layout.origin, layoutRotation: layout.rotation },
    ];
    for (const variant of ["offline", "one_slot", "two_slot"] as const) {
      layout[variant].forEach((element, index) =>
        targets.push({
          path: `${prefix}.${variant}.${index}`,
          bind: element.bind,
          day,
          point: element.offset,
          origin: layout.origin,
          layoutRotation: layout.rotation,
        }),
      );
    }
    return targets;
  });
  return [...globals, ...days];
}

export function positionMetadata(metadata: ArtMetadata, path: string, point: number[]): ArtMetadata {
  const target = metadataTargets(metadata).find((target) => target.path === path);
  if (!target) return metadata;
  if (point.length !== 2 || !point.every(Number.isInteger)) return metadata;
  if (target.point.every((value, index) => value === point[index])) return metadata;
  const next = structuredClone(metadata);
  const nextTarget = metadataTargets(next).find((target) => target.path === path)!;
  nextTarget.point.splice(0, nextTarget.point.length, ...point);
  return { ...next, styles: metadata.styles, formats: metadata.formats };
}
